import React, { ReactNode, useCallback, useMemo } from 'react';
import type {
  DatesSetArg,
  DayCellContentArg,
  EventContentArg,
} from '@fullcalendar/core';

import { CalendarControls } from './components/calendar-controls';
import { CalendarFilters } from './components/calendar-filters';
import { Spinner, Wrapper } from './components/calendar-layout';
import { CalendarView } from './components/calendar-view';
import { useCalendarData } from './hooks/useCalendarData';
import { useCalendarFilters } from './hooks/useCalendarFilters';
import { useCalendarState } from './hooks/useCalendarState';

interface CalendarFeatureProps {
  rightActions?: ReactNode[];
  renderDayCell: (dayRenderInfo: DayCellContentArg) => React.ReactNode;
  renderEventContent: (eventInfo: EventContentArg) => React.ReactNode;
}

export const CalendarFeature = ({
  rightActions = [],
  renderDayCell,
  renderEventContent,
}: CalendarFeatureProps) => {
  const {
    searchVal,
    setSearchVal,
    filters,
    dateRange,
    buildCalendarFilters,
    handleSearch,
    handleApplyFilter,
    handleDateRangeChange,
  } = useCalendarFilters();

  const { initialView, today, handleDateChange, handleViewChange } =
    useCalendarState();

  const calendarFilters = useMemo(() => {
    return buildCalendarFilters(dateRange);
  }, [buildCalendarFilters, dateRange]);

  const { calendarEvents, isLoading } = useCalendarData(calendarFilters);

  const handleCalendarDateChange = useCallback(
    (dateInfo: DatesSetArg) => {
      const dates = handleDateChange(dateInfo);
      if (dates && dates.length === 2) {
        handleDateRangeChange([dates[0], dates[1]]);
      }
    },
    [handleDateChange, handleDateRangeChange],
  );

  return (
    <Wrapper id="grab-x__calendar-page">
      <CalendarFilters
        searchVal={searchVal}
        onSearchChange={setSearchVal}
        onSearch={handleSearch}
        filters={filters}
        onApplyFilter={handleApplyFilter}
        rightActions={rightActions}
      />
      <CalendarControls view={initialView} onViewChange={handleViewChange} />
      {isLoading && <Spinner />}
      <CalendarView
        initialView={initialView}
        today={today}
        events={calendarEvents}
        onDateChange={handleCalendarDateChange}
        renderDayCell={renderDayCell}
        renderEventContent={renderEventContent}
      />
    </Wrapper>
  );
};
